'use client';

import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, Typography, InputBase } from '@mui/material';
import { Icon } from '@/components/icons';
import { APP_MAX_WIDTH } from '@/components/PageContent/PageContent';
import { SAFE_AREA_TOP } from '@/contexts/SafeAreaContext';
import { Z_INDEX } from '@/constants/zIndex';
import { TEMP_FG_INVERSE, TEMP_FG_DIAMOND } from '@/constants/tempColors';
import {
  TEMP_TITLE_BOLD_M,
  TEMP_TEXT_SEMIBOLD_L,
  TEMP_TEXT_SEMIBOLD_XL,
} from '@/constants/tempTypo';
import { useTranslator } from '@/lang';
import ConsentBottomSheet from './ConsentBottomSheet';

interface OnboardingOverlayProps {
  onComplete: (
    name: string,
    birthDate?: string,
    referralCode?: string,
    isMarketingAgreed?: boolean,
  ) => void;
  onSkip: () => void;
}

type Step = 'name' | 'birthDate' | 'referral';

interface PendingComplete {
  name: string;
  birthDate?: string;
  referralCode?: string;
}

const EASING: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

export default function OnboardingOverlay({ onComplete, onSkip }: OnboardingOverlayProps) {
  const { t } = useTranslator();
  const [step, setStep] = useState<Step>('name');
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [referralCode, setReferralCode] = useState(() => {
    if (typeof window === 'undefined') return '';
    const saved = localStorage.getItem('museling_referral_code');
    if (saved) localStorage.removeItem('museling_referral_code');
    return saved || '';
  });
  const [direction, setDirection] = useState<1 | -1>(1);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [pendingComplete, setPendingComplete] = useState<PendingComplete | null>(null);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const handleResize = () => {
      const kbHeight = window.innerHeight - vv.height;
      setKeyboardHeight(kbHeight > 50 ? kbHeight : 0);
    };

    vv.addEventListener('resize', handleResize);
    return () => vv.removeEventListener('resize', handleResize);
  }, []);

  const handleNext = useCallback(() => {
    if (step === 'name' && name.trim()) {
      setDirection(1);
      setStep('birthDate');
    } else if (step === 'birthDate') {
      setDirection(1);
      setStep('referral');
    } else if (step === 'referral') {
      const formatted = birthDate.replace(/\./g, '');
      const trimmedCode = referralCode.trim().toUpperCase();
      setPendingComplete({
        name: name.trim(),
        birthDate: formatted.length === 8 ? formatted : undefined,
        referralCode: trimmedCode.length === 5 ? trimmedCode : undefined,
      });
    }
  }, [step, name, birthDate, referralCode]);

  const handleBack = useCallback(() => {
    if (step === 'birthDate') {
      setDirection(-1);
      setStep('name');
    } else if (step === 'referral') {
      setDirection(-1);
      setStep('birthDate');
    }
  }, [step]);

  const handleSkip = useCallback(() => {
    if (step === 'name') {
      setDirection(1);
      setStep('birthDate');
    } else if (step === 'birthDate') {
      setDirection(1);
      setStep('referral');
    } else if (step === 'referral') {
      const formatted = birthDate.replace(/\./g, '');
      setPendingComplete({
        name: name.trim(),
        birthDate: formatted.length === 8 ? formatted : undefined,
        referralCode: undefined,
      });
    }
  }, [step, name, birthDate]);

  const handleConsentAgree = useCallback(
    (marketingAgreed: boolean) => {
      if (!pendingComplete) return;
      const { name: n, birthDate: b, referralCode: r } = pendingComplete;
      setPendingComplete(null);
      onComplete(n || t('onboarding.default_name'), b, r, marketingAgreed);
    },
    [pendingComplete, onComplete, t],
  );

  const handleConsentBack = useCallback(() => {
    setPendingComplete(null);
  }, []);

  const handleBirthDateChange = (value: string) => {
    const digits = value.replace(/[^\d]/g, '');
    if (digits.length <= 8) {
      let formatted = digits;
      if (digits.length > 4) {
        formatted = digits.slice(0, 4) + '.' + digits.slice(4);
      }
      if (digits.length > 6) {
        formatted = digits.slice(0, 4) + '.' + digits.slice(4, 6) + '.' + digits.slice(6);
      }
      setBirthDate(formatted);
    }
  };

  const handleReferralCodeChange = (value: string) => {
    const filtered = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    if (filtered.length <= 5) {
      setReferralCode(filtered);
    }
  };

  const isNextEnabled =
    step === 'name'
      ? name.trim().length > 0
      : step === 'birthDate'
        ? birthDate.replace(/\./g, '').length === 8
        : true;

  const slideVariants = {
    enter: (dir: number) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir > 0 ? '-100%' : '100%', opacity: 0 }),
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        maxWidth: APP_MAX_WIDTH,
        mx: 'auto',
        zIndex: Z_INDEX.IMAGE_VIEWER + 10,
        background: 'linear-gradient(180deg, #7500CE 0%, #2100A5 100%)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        textShadow: '0 1px 3px rgba(0,0,0,0.55)',
      }}
    >
      {/* 배경 영상 — 전체 화면, 루프 */}
      <Box
        component="video"
        src="/videos/onboarding_1.mp4"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      {/* 가독성 오버레이 — 상하단 어둡게, 가운데는 영상 살림 */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 28%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.35) 75%, rgba(0,0,0,0.65) 100%)',
        }}
      />

      {/* 헤더: 뒤로가기 + 건너뛰기 */}
      <Box
        sx={{
          pt: `calc(12px + ${SAFE_AREA_TOP})`,
          px: '16px',
          pb: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          onClick={step === 'name' ? undefined : handleBack}
          sx={{
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: step === 'name' ? 'default' : 'pointer',
            opacity: step === 'name' ? 0 : 1,
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <Icon name="icChevronLeft" size={24} color={TEMP_FG_INVERSE} />
        </Box>

        <Typography
          onClick={handleSkip}
          sx={{
            ...TEMP_TEXT_SEMIBOLD_L,
            color: 'rgba(255, 255, 255, 0.8)',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {t('onboarding.skip')}
        </Typography>
      </Box>

      {/* 콘텐츠 영역 */}
      <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden', zIndex: 1 }}>
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <motion.div
            key={step}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: 'tween', duration: 0.3, ease: EASING }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              padding: '0 24px',
            }}
          >
            {step === 'name' ? (
              <NameStep name={name} onChange={setName} />
            ) : step === 'birthDate' ? (
              <BirthDateStep birthDate={birthDate} onChange={handleBirthDateChange} />
            ) : (
              <ReferralStep referralCode={referralCode} onChange={handleReferralCodeChange} />
            )}
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* 하단 안내 + 다음 버튼 */}
      <Box
        sx={{
          px: '24px',
          pb: keyboardHeight > 0 ? `${keyboardHeight + 12}px` : '40px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          flexShrink: 0,
          transition: 'padding-bottom 0.25s ease-out',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {keyboardHeight === 0 && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 500,
                lineHeight: 1.5,
                color: 'rgba(255, 255, 255, 0.6)',
              }}
            >
              {step === 'referral'
                ? t('onboarding.referral_skip_hint')
                : t('onboarding.privacy_hint_1')}
            </Typography>
            {step !== 'referral' && (
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  lineHeight: 1.5,
                  color: 'rgba(255, 255, 255, 0.6)',
                }}
              >
                {t('onboarding.privacy_hint_2')}
              </Typography>
            )}
          </Box>
        )}

        <Box
          onClick={isNextEnabled ? handleNext : undefined}
          sx={{
            height: 56,
            borderRadius: '16px',
            bgcolor: isNextEnabled ? '#FFFFFF' : 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isNextEnabled ? 'pointer' : 'default',
            transition: 'background-color 0.2s',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <Typography
            sx={{
              ...TEMP_TEXT_SEMIBOLD_XL,
              color: isNextEnabled ? '#4b00cc' : 'rgba(255, 255, 255, 0.4)',
              textShadow: 'none',
            }}
          >
            {step === 'referral'
              ? referralCode.length === 5
                ? t('onboarding.done')
                : t('onboarding.skip')
              : t('onboarding.next')}
          </Typography>
        </Box>
      </Box>

      <ConsentBottomSheet
        open={pendingComplete !== null}
        onAgree={handleConsentAgree}
        onBack={handleConsentBack}
      />
    </Box>
  );
}

function NameStep({ name, onChange }: { name: string; onChange: (v: string) => void }) {
  const { t } = useTranslator();
  return (
    <>
      <Box sx={{ mt: '40px', textAlign: 'center' }}>
        <Typography
          sx={{
            ...TEMP_TITLE_BOLD_M,
            color: TEMP_FG_INVERSE,
            lineHeight: 1.5,
          }}
        >
          {t('onboarding.name_title_1')}
        </Typography>
        <Typography
          sx={{
            ...TEMP_TITLE_BOLD_M,
            color: TEMP_FG_INVERSE,
            lineHeight: 1.5,
          }}
        >
          {t('onboarding.name_title_2')}
        </Typography>
      </Box>

      <Box sx={{ mt: '40px', display: 'flex', justifyContent: 'center' }}>
        <InputBase
          value={name}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t('onboarding.name_placeholder')}
          autoFocus
          sx={{
            fontSize: 28,
            fontWeight: 700,
            color: TEMP_FG_INVERSE,
            textAlign: 'center',
            width: '100%',
            '& input': {
              textAlign: 'center',
              '&::placeholder': {
                color: 'rgba(255, 255, 255, 0.3)',
                opacity: 1,
              },
            },
          }}
        />
      </Box>
    </>
  );
}

function BirthDateStep({ birthDate, onChange }: { birthDate: string; onChange: (v: string) => void }) {
  const { t } = useTranslator();
  return (
    <>
      <Box sx={{ mt: '40px', textAlign: 'center' }}>
        <Typography
          sx={{
            ...TEMP_TITLE_BOLD_M,
            color: TEMP_FG_INVERSE,
            lineHeight: 1.5,
          }}
        >
          {t('onboarding.birth_title_1')}
        </Typography>
        <Typography
          sx={{
            ...TEMP_TITLE_BOLD_M,
            color: TEMP_FG_INVERSE,
            lineHeight: 1.5,
          }}
        >
          {t('onboarding.birth_title_2')}
        </Typography>
      </Box>

      <Box sx={{ mt: '40px', display: 'flex', justifyContent: 'center' }}>
        <InputBase
          value={birthDate}
          onChange={(e) => onChange(e.target.value)}
          placeholder="1999.06.24"
          autoFocus
          inputMode="numeric"
          sx={{
            fontSize: 28,
            fontWeight: 700,
            color: TEMP_FG_INVERSE,
            textAlign: 'center',
            width: '100%',
            '& input': {
              textAlign: 'center',
              '&::placeholder': {
                color: 'rgba(255, 255, 255, 0.3)',
                opacity: 1,
              },
            },
          }}
        />
      </Box>
    </>
  );
}

function ReferralStep({ referralCode, onChange }: { referralCode: string; onChange: (v: string) => void }) {
  const { t } = useTranslator();
  return (
    <>
      <Box sx={{ mt: '40px', textAlign: 'center' }}>
        <Typography
          sx={{
            ...TEMP_TITLE_BOLD_M,
            color: TEMP_FG_INVERSE,
            lineHeight: 1.5,
            whiteSpace: 'nowrap',
          }}
        >
          {t('onboarding.referral_title')}
        </Typography>
      </Box>

      <Box sx={{ mt: '40px', display: 'flex', justifyContent: 'center' }}>
        <InputBase
          value={referralCode}
          onChange={(e) => onChange(e.target.value)}
          placeholder="AB3K7"
          autoFocus
          autoCapitalize="characters"
          sx={{
            fontSize: 32,
            fontWeight: 800,
            color: TEMP_FG_INVERSE,
            textAlign: 'center',
            width: '100%',
            letterSpacing: '8px',
            '& input': {
              textAlign: 'center',
              '&::placeholder': {
                color: 'rgba(255, 255, 255, 0.3)',
                opacity: 1,
                letterSpacing: '8px',
              },
            },
          }}
        />
      </Box>
    </>
  );
}
